import React from 'react';
import { useShareSheetQueue } from '../../hooks/useShareSheetQueue';
import { useMesh } from '../../hooks/useMesh';
import ShareSheet from './ShareSheet';

/**
 * F14.2 — Singleton host for the receiver-consent share-sheet.
 *
 * Mounted once at the app root inside MeshProvider's subtree. Consumes the
 * Wave 2A `useShareSheetQueue()` store, which surfaces the next pending
 * `file-offer` envelope (after the dispatcher has filtered out
 * session-blocked senders).
 *
 * Sender metadata (handle + avatar seed) is looked up from the live mesh
 * peer list by `fromId`. If the sender has already left the room mid-offer,
 * we still render the sheet with a generic "Unknown sender" label rather
 * than crashing — the receiver can still accept/decline; the offer envelope
 * carries enough info to do so.
 */
export function ShareSheetHost() {
  const { current, accept, decline } = useShareSheetQueue();
  const { peers } = useMesh();

  if (!current) return null;

  const sender = Array.isArray(peers)
    ? peers.find((p) => p && p.userId === current.fromId)
    : null;

  const senderName = sender?.handle || 'Unknown sender';
  const senderAvatarSeed = sender?.avatarSeed || current.fromId;

  return (
    <ShareSheet
      offer={current}
      senderName={senderName}
      senderAvatarSeed={senderAvatarSeed}
      onAccept={() => accept()}
      onDecline={() => decline({})}
      onDeclineAndBlock={() => decline({ block: true })}
    />
  );
}

export default ShareSheetHost;
